import { Card, CardContent } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronRight as ChevronRightIcon,
  ExternalLink,
  ListX,
} from 'lucide-react'
import { Heading } from '@/components/ui/typography'
import type {
  VoicePipelineSessionRowItem,
  VoicePipelineMetrics,
} from '@/lib/api/reporting.client'
import { reportingApiClient } from '@/lib/api/reporting.client'
import {
  buildActivityDeeplinkPath,
  getLanguageLabel,
} from '@super/shared-types'
import { useState, useCallback } from 'react'

interface ColumnDef {
  key: string
  label: string
  format: (row: VoicePipelineSessionRowItem) => string
}

const COLUMNS: ColumnDef[] = [
  {
    key: 'createdAt',
    label: 'Time',
    format: r =>
      new Date(r.createdAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }),
  },
  {
    key: 'turnCount',
    label: 'Turns',
    format: r => r.turnCount.toLocaleString(),
  },
  {
    key: 'avgLatency',
    label: 'Avg Latency',
    format: r => `${Math.round(r.avgLatency * 1000)} ms`,
  },
  {
    key: 'avgLlmTtft',
    label: 'LLM TTFT',
    format: r => `${Math.round(r.avgLlmTtft * 1000)} ms`,
  },
  {
    key: 'avgTtsTtfb',
    label: 'TTS TTFB',
    format: r => `${Math.round(r.avgTtsTtfb * 1000)} ms`,
  },
  {
    key: 'avgEouDelay',
    label: 'EOU Delay',
    format: r => `${Math.round(r.avgEouDelay * 1000)} ms`,
  },
  {
    key: 'avgPromptTokens',
    label: 'Avg Prompt Tkns',
    format: r => Math.round(r.avgPromptTokens).toLocaleString(),
  },
  {
    key: 'maxPromptTokens',
    label: 'Max Prompt Tkns',
    format: r => r.maxPromptTokens.toLocaleString(),
  },
  {
    key: 'llmInstructionTokens',
    label: 'Instruct Tkns',
    format: r =>
      r.llmInstructionTokens != null
        ? r.llmInstructionTokens.toLocaleString()
        : '-',
  },
  {
    key: 'llmKbTokens',
    label: 'KB Tkns',
    format: r => (r.llmKbTokens != null ? r.llmKbTokens.toLocaleString() : '-'),
  },
  {
    key: 'sttModel',
    label: 'STT Model',
    format: r => r.sttModel ?? '-',
  },
  {
    key: 'llmModel',
    label: 'LLM Model',
    format: r => r.llmModel ?? '-',
  },
  {
    key: 'ttsModel',
    label: 'TTS Model',
    format: r => r.ttsModel ?? '-',
  },
]

// Total columns = COLUMNS.length + 1 (expand chevron) + 1 (external link)
const TOTAL_COLS = COLUMNS.length + 2

interface SessionsTableProps {
  sessions: VoicePipelineSessionRowItem[]
  totalCount: number
  page: number
  pageSize: number
  sortBy: string
  sortDir: 'asc' | 'desc'
  onSort: (column: string) => void
  onPageChange: (page: number) => void
  isLoading: boolean
}

function SortIcon({
  column,
  sortBy,
  sortDir,
}: {
  column: string
  sortBy: string
  sortDir: 'asc' | 'desc'
}) {
  if (column !== sortBy) return <ArrowUpDown className="h-3 w-3 ml-1" />
  return sortDir === 'asc' ? (
    <ArrowUp className="h-3 w-3 ml-1" />
  ) : (
    <ArrowDown className="h-3 w-3 ml-1" />
  )
}

function fmtMs(val: number | undefined | null): string {
  if (val == null) return '-'
  return `${Math.round(val * 1000)} ms`
}

function fmtNum(val: number | undefined | null): string {
  if (val == null) return '-'
  return Math.round(val).toLocaleString()
}

function TurnSubTable({ turns }: { turns: VoicePipelineMetrics[] }) {
  return (
    <div className="overflow-x-auto bg-gray-50 dark:bg-muted/30 border-t border-gray-100 dark:border-border">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-gray-200 dark:border-border bg-gray-100 dark:bg-muted">
            <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-muted-foreground whitespace-nowrap">
              Turn #
            </th>
            <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-muted-foreground whitespace-nowrap">
              Total Latency
            </th>
            <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-muted-foreground whitespace-nowrap">
              LLM TTFT
            </th>
            <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-muted-foreground whitespace-nowrap">
              TTS TTFB
            </th>
            <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-muted-foreground whitespace-nowrap">
              Classification
            </th>
            <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-muted-foreground whitespace-nowrap">
              Prompt Tkns
            </th>
            <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-muted-foreground whitespace-nowrap">
              Language
            </th>
          </tr>
        </thead>
        <tbody>
          {turns.map(turn => (
            <tr
              key={turn.id}
              className="border-b border-gray-100 dark:border-border hover:bg-gray-100/60 dark:hover:bg-muted/60"
            >
              <td className="px-3 py-1.5 text-gray-700 dark:text-foreground">
                {turn.turnIndex}
              </td>
              <td className="px-3 py-1.5 text-gray-700 dark:text-foreground">
                {fmtMs(turn.totalLatency)}
              </td>
              <td className="px-3 py-1.5 text-gray-700 dark:text-foreground">
                {fmtMs(turn.llmTtft)}
              </td>
              <td className="px-3 py-1.5 text-gray-700 dark:text-foreground">
                {fmtMs(turn.ttsTtfb)}
              </td>
              <td className="px-3 py-1.5 text-gray-700 dark:text-foreground">
                {fmtMs(turn.scenarioClassificationLatency)}
              </td>
              <td className="px-3 py-1.5 text-gray-700 dark:text-foreground">
                {fmtNum(turn.llmPromptTokens)}
              </td>
              <td className="px-3 py-1.5 text-gray-700 dark:text-foreground">
                {getLanguageLabel(turn.language)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ExpandableRow({
  row,
  columns,
  onRowClick,
}: {
  row: VoicePipelineSessionRowItem
  columns: ColumnDef[]
  onRowClick: (row: VoicePipelineSessionRowItem) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const [turns, setTurns] = useState<VoicePipelineMetrics[] | null>(null)
  const [loading, setLoading] = useState(false)

  const handleToggle = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation()
      if (!expanded && turns === null) {
        setLoading(true)
        try {
          const data = await reportingApiClient.getVoicePipelineSessionTurns(
            row.conferenceId,
          )
          setTurns(data ?? [])
        } catch {
          setTurns([])
        } finally {
          setLoading(false)
        }
      }
      setExpanded(prev => !prev)
    },
    [expanded, turns, row.conferenceId],
  )

  return (
    <>
      <TableRow
        className={
          row.threadId
            ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-muted transition-colors'
            : 'opacity-60'
        }
        onClick={() => onRowClick(row)}
      >
        <TableCell className="w-8 pr-0">
          <button
            className="flex items-center justify-center w-6 h-6 rounded hover:bg-gray-200 dark:hover:bg-muted transition-colors"
            onClick={handleToggle}
            aria-label={expanded ? 'Collapse turns' : 'Expand turns'}
          >
            {loading ? (
              <span className="h-3 w-3 border border-gray-400 dark:border-muted-foreground border-t-transparent rounded-full animate-spin" />
            ) : expanded ? (
              <ChevronDown className="h-3.5 w-3.5 text-gray-500 dark:text-muted-foreground" />
            ) : (
              <ChevronRightIcon className="h-3.5 w-3.5 text-gray-500 dark:text-muted-foreground" />
            )}
          </button>
        </TableCell>
        {columns.map(col => (
          <TableCell key={col.key} className="whitespace-nowrap text-sm">
            {col.format(row)}
          </TableCell>
        ))}
        <TableCell className="w-8">
          {row.threadId && (
            <ExternalLink className="h-3.5 w-3.5 text-gray-400 dark:text-muted-foreground" />
          )}
        </TableCell>
      </TableRow>
      {expanded && turns !== null && (
        <TableRow>
          <TableCell colSpan={TOTAL_COLS} className="p-0">
            <TurnSubTable turns={turns} />
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

export function SessionsTable({
  sessions,
  totalCount,
  page,
  pageSize,
  sortBy,
  sortDir,
  onSort,
  onPageChange,
  isLoading,
}: SessionsTableProps) {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))

  const handleRowClick = (row: VoicePipelineSessionRowItem) => {
    if (!row.threadId) return
    const path = buildActivityDeeplinkPath({
      threadId: row.threadId,
      sessionId: row.sessionId,
      companyId: row.companyId,
    })
    window.open(path, '_blank')
  }

  return (
    <Card className="bg-white dark:bg-background shadow-sm border border-gray-200 dark:border-border hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <Heading variant="section-title" className="mb-1">
              Sessions
            </Heading>
            <p className="text-sm text-gray-500 dark:text-muted-foreground">
              {totalCount.toLocaleString()} session
              {totalCount !== 1 ? 's' : ''} found · click row to expand turns
            </p>
          </div>
        </div>

        {isLoading && sessions.length === 0 ? (
          <div className="space-y-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : sessions.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-[300px] text-center px-4">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-muted flex items-center justify-center mb-4">
              <ListX className="h-8 w-8 text-gray-400 dark:text-muted-foreground" />
            </div>
            <p className="text-gray-500 dark:text-muted-foreground font-medium mb-1">
              No sessions found
            </p>
            <p className="text-sm text-gray-400 dark:text-muted-foreground">
              No pipeline sessions for the selected period
            </p>
          </div>
        ) : (
          <>
            <div
              className={`overflow-auto max-h-[70vh] [&>div]:overflow-visible transition-opacity ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}
            >
              <Table>
                <TableHeader className="sticky top-0 z-10 bg-white dark:bg-background">
                  <TableRow>
                    <TableHead className="w-8" />
                    {COLUMNS.map(col => (
                      <TableHead
                        key={col.key}
                        className="cursor-pointer select-none hover:bg-gray-50 dark:hover:bg-muted whitespace-nowrap"
                        onClick={() => onSort(col.key)}
                      >
                        <div className="flex items-center">
                          {col.label}
                          <SortIcon
                            column={col.key}
                            sortBy={sortBy}
                            sortDir={sortDir}
                          />
                        </div>
                      </TableHead>
                    ))}
                    <TableHead className="w-8" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.map(row => (
                    <ExpandableRow
                      key={row.conferenceId}
                      row={row}
                      columns={COLUMNS}
                      onRowClick={handleRowClick}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100 dark:border-border">
              <p className="text-sm text-gray-500 dark:text-muted-foreground">
                Page {page} of {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onPageChange(page - 1)}
                  disabled={page <= 1}
                  className="h-8"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onPageChange(page + 1)}
                  disabled={page >= totalPages}
                  className="h-8"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
